import { useMemo } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { get } from 'lodash-es';
import type {
  EntityDetail,
  EnvironmentKey,
  TreeNode,
} from '@web-config-app/core';
import { getBreadcrumbsForProperty } from '../../utilities/get-breadcrumbs-for-property/get-breadcrumbs-for-property';
import { parseEntityData } from './parse-entity-data';
import { useAuthoringEnvironments } from '../use-authoring-environments/use-authoring-environments';
import { DataTableRow } from '../../types/entity-table.types';

export interface UseFormatEntityTableDataProps {
  currentEnvironmentData: EnvironmentData;
  otherEnvironmentData?: EnvironmentData;

  entityTreeNodeMap: Map<string, TreeNode>;
  /**
   * Optional path to nested data.
   */
  dataPath?: string;
}

export interface UseFormatEntityTableDataResult {
  rows: DataTableRow[];
  isMultiEnvironment: boolean;
  columnHeaders: { key: string; displayLabel: string }[];
}

export type ParsedEntityData = { path: string; data?: any; otherData?: any }[];
export interface EnvironmentData {
  key: EnvironmentKey;
  data: Partial<EntityDetail> | undefined;
}

export const useFormatEntityTableData = ({
  currentEnvironmentData,
  otherEnvironmentData,
  entityTreeNodeMap,
  dataPath,
}: UseFormatEntityTableDataProps): UseFormatEntityTableDataResult => {
  const { formatMessage } = useIntl();
  const { getEnvironmentLabel } = useAuthoringEnvironments();

  return useMemo(() => {
    const isEmptyValue = (value: any): boolean => {
      if (value === null || value === undefined) return true;
      if (value === '') return true;
      if (Array.isArray(value) && value.length === 0) return true;
      if (typeof value === 'object' && Object.keys(value).length === 0)
        return true;
      return false;
    };

    const sortPathsByBreadcrumbs = (paths: string[]): string[] => {
      const total = paths.length;
      if (total <= 1) return paths.slice();

      const collator = new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: 'base',
      });

      const computeLabel = (path: string): string => {
        const breadcrumbs = getBreadcrumbsForProperty({
          path,
          data: currentEnvironmentData?.data,
          pathPrefix: dataPath,
          entityTreeNodeMap,
          translate: formatMessage,
        });
        return breadcrumbs
          .map(({ label: breadcrumb }) => breadcrumb)
          .join(' → ');
      };

      return paths
        .map((path) => ({ path, label: computeLabel(path) }))
        .sort((a, b) => collator.compare(a.label, b.label))
        .map((item) => item.path);
    };
    const currentData = dataPath
      ? get(currentEnvironmentData?.data, dataPath)
      : currentEnvironmentData?.data;

    const otherData = dataPath
      ? get(otherEnvironmentData?.data, dataPath)
      : otherEnvironmentData?.data;

    const currentDataWithTranslations = (() => {
      if (
        dataPath === 'attributes.domainEntityAttributes' &&
        currentEnvironmentData?.data?.attributes?.translations
      ) {
        return {
          ...currentData,
          translations: currentEnvironmentData.data.attributes.translations,
        };
      }
      return currentData;
    })();

    const otherDataWithTranslations = (() => {
      if (
        dataPath === 'attributes.domainEntityAttributes' &&
        otherEnvironmentData?.data?.attributes?.translations
      ) {
        return {
          ...otherData,
          translations: otherEnvironmentData.data.attributes.translations,
        };
      }
      return otherData;
    })();

    const parsedCurrentEnvironmentData =
      parseEntityData(currentDataWithTranslations, formatMessage, dataPath) ||
      [];

    if (otherEnvironmentData?.data === undefined) {
      const paths = parsedCurrentEnvironmentData.map(({ path }) => path);
      const sortedPaths = sortPathsByBreadcrumbs(paths);

      return {
        isMultiEnvironment: false,
        rows: sortedPaths
          .map((path) => {
            const item = parsedCurrentEnvironmentData.find(
              (parsed) => parsed.path === path,
            );
            if (!item) return null;

            const { data } = item;
            const breadcrumbs = getBreadcrumbsForProperty({
              path,
              data: currentEnvironmentData?.data,
              pathPrefix: dataPath,
              entityTreeNodeMap,
              translate: formatMessage,
            });
            const label = breadcrumbs
              .map(({ label: breadcrumb }) => breadcrumb)
              .join(' → ');

            return {
              path,
              data,
              label,
            };
          })
          .filter(
            (item): item is NonNullable<typeof item> =>
              Boolean(item?.label) && !isEmptyValue(item?.data),
          ),
        columnHeaders: [
          {
            key: 'label',
            displayLabel: formatMessage({ id: 'PATH' }),
          },
          {
            key: 'data',
            displayLabel: formatMessage({ id: 'VALUE' }),
          },
        ],
      };
    }

    const parsedOtherEnvironmentData =
      parseEntityData(otherDataWithTranslations, formatMessage, dataPath) || [];

    const paths: string[] = Array.from(
      new Set(
        [
          ...parsedCurrentEnvironmentData,
          ...(parsedOtherEnvironmentData ?? []),
        ].map(({ path }) => path),
      ),
    );

    const sortedPaths = sortPathsByBreadcrumbs(paths);

    return {
      isMultiEnvironment: true,
      rows: sortedPaths
        .map((path) => {
          const currentItem = parsedCurrentEnvironmentData.find(
            (item) => item.path === path,
          );
          const otherItem = parsedOtherEnvironmentData.find(
            (item) => item.path === path,
          );
          const breadcrumbs = getBreadcrumbsForProperty({
            path,
            data: currentEnvironmentData?.data,
            pathPrefix: dataPath,
            entityTreeNodeMap,
            translate: formatMessage,
          });
          const label = breadcrumbs
            .map(({ label: breadcrumb }) => breadcrumb)
            .join(' → ');

          return {
            path,
            label,
            data: currentItem?.data,
            otherData: otherItem?.data,
          };
        })
        .filter((item) => {
          const hasLabel = Boolean(item.label);
          const bothEmpty =
            isEmptyValue(item.data) && isEmptyValue(item.otherData);
          return hasLabel && !bothEmpty;
        }),
      columnHeaders: [
        {
          key: 'label',
          displayLabel: formatMessage({ id: 'PATH' }),
        },
        {
          key: 'data',
          displayLabel: formatMessage(
            { id: 'DATA_FOR_ENVIRONMENT' },
            {
              environment: getEnvironmentLabel(currentEnvironmentData.key),
            },
          ),
        },
        {
          key: 'otherData',
          displayLabel: formatMessage(
            { id: 'DATA_FOR_ENVIRONMENT' },
            {
              environment: getEnvironmentLabel(otherEnvironmentData.key),
            },
          ),
        },
      ],
    };
  }, [
    currentEnvironmentData.data,
    otherEnvironmentData?.data,
    currentEnvironmentData.key,
    otherEnvironmentData?.key,
    formatMessage,
    getEnvironmentLabel,
    dataPath,
    entityTreeNodeMap,
  ]);
};
